//src/services/dashboardService.js
import apiClient from './apiClient';

export const dashboardService = {
  // Fetch dashboard statistics
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getDashboardStatistics: async () => {
    try {
      console.log('[dashboardService] Fetching dashboard statistics...');
      const response = await apiClient.get(`/dashboard/statistics`);
      console.log('[dashboardService] Dashboard statistics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('[dashboardService] Error fetching dashboard statistics:', error);
      console.error('[dashboardService] Error response:', error.response?.data);
      // If it's a 500 error about missing tables, return empty/default data instead of throwing
      // This allows the dashboard to still render with fallback data
      if (error.response?.status === 500) {
        console.warn('[dashboardService] Backend error (possibly missing organization tables), returning default data');
        return {
          totalDoctors: 0,
          totalStaff: 0,
          totalDepartments: 0,
          totalMedicines: 0,
          totalIncome: 0,
          totalExpenses: 0,
          totalRevenue: 0,
          oosMedicines: 0,
          expiredStocks: 0,
        };
      }
      throw error;
    }
  },

  // Fetch income vs expenses data for a specific year
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getIncomeExpensesData: async (year) => {
    try {
      const response = await apiClient.get(`/dashboard/income-expenses/${year}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching income vs expenses data:', error);
      throw error;
    }
  },

  // Fetch income per department data for a specific month and year
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getIncomePerDepartmentData: async (month, year) => {
    try {
      console.log(`[dashboardService] Fetching income per department - month: ${month}, year: ${year}`);
      const response = await apiClient.get(`/dashboard/income-per-department?month=${month}&year=${year}`);
      console.log('[dashboardService] Income per department response:', response.data);
      // Check if response includes organizationId in request
      const requestConfig = response.config;
      const orgIdInRequest = requestConfig?.params?.organizationId || requestConfig?.params?.orgId;
      console.log(`[dashboardService] organizationId sent in request: ${orgIdInRequest}`);
      return response.data;
    } catch (error) {
      console.error('[dashboardService] Error fetching income per department data:', error);
      console.error('[dashboardService] Error response:', error.response?.data);
      throw error;
    }
  },

  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getPatientSummaryData: async (month, year) => {
    try {
      const response = await apiClient.get(`/dashboard/patient-summary?month=${month}&year=${year}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching patient summary data:', error);
      throw error;
    }
  },

  // Fetch doctors data
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getDoctors: async () => {
    try {
      const response = await apiClient.get(`/doctors`);
      return response.data;
    } catch (error) {
      console.error('Error fetching doctors:', error);
      throw error;
    }
  },

  // Fetch staff data
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getStaff: async () => {
    try {
      const response = await apiClient.get(`/staff`);
      return response.data;
    } catch (error) {
      console.error('Error fetching staff:', error);
      throw error;
    }
  },

  // Fetch departments data
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getDepartments: async () => {
    try {
      const response = await apiClient.get(`/departments`);
      return response.data;
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  },

  // Fetch invoices data
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getInvoices: async () => {
    try {
      const response = await apiClient.get(`/invoices`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  },

  // Fetch medicine inventory data
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getMedicineInventory: async () => {
    try {
      const response = await apiClient.get(`/medicinesinventory`);
      return response.data;
    } catch (error) {
      console.error('Error fetching medicine inventory:', error);
      throw error;
    }
  },

  // Fetch top 5 medicines going OOS
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getTopOOSMedicines: async () => {
    try {
      const response = await apiClient.get(`/dashboard/top-oos-medicines`);
      return response.data;
    } catch (error) {
      console.error('Error fetching top OOS medicines:', error);
      throw error;
    }
  },

  // Fetch top 5 path items going OOS (path inventory)
  getTopOOSPathItems: async () => {
    try {
      const response = await apiClient.get(`/dashboard/top-oos-path-items`);
      return response.data;
    } catch (error) {
      console.error('Error fetching top OOS path items:', error);
      throw error;
    }
  },

  // Fetch gender-wise patients per department
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getGenderWisePatientsPerDepartment: async (month, year) => {
    try {
      const response = await apiClient.get(`/dashboard/gender-wise-patients?month=${month}&year=${year}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching gender-wise patients per department:', error);
      throw error;
    }
  },

  // Fetch patients per department
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getPatientsPerDepartment: async (month, year) => {
    try {
      const response = await apiClient.get(`/dashboard/patients-per-department?month=${month}&year=${year}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching patients per department:', error);
      throw error;
    }
  },

  // Fetch available doctors with visiting hours
  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  getAvailableDoctors: async () => {
    try {
      const response = await apiClient.get(`/dashboard/available-doctors`);
      return response.data;
    } catch (error) {
      console.error('Error fetching available doctors:', error);
      throw error;
    }
  }
};